import { createRouter, createWebHistory } from 'vue-router'
import store from 'src/vuex'
import { SubmissionObject } from 'src/pages/submissions/script/common.data'
import { confirmBulkUser } from 'src/utils/helper/confirm-bulk-user'
import { BULK_IMPORT, DASHBOARD, EARNINGS_REPORT_DASHBOARD, PAYMENT_ACCOUNT, PAYMENT_ACCOUNTS_EDIT_MANAGE, PAYMENT_ACCOUNTS_NEW_MANAGE, PAYMENT_DASHBOARD, VERIFY_EMAIL, TERMINATED_CONTRACT, QUARTERLY_STATEMENTS } from 'src/utils/constants/app-routes-name';
import { shouldRedirectToDashboard, shouldRedirectToVerifyEmailPage } from 'src/config/router/callbacks'
import { ERROR_PAGES } from 'src/utils/constants/common'
import { applyURLFeatureFlags } from 'modules/feature-flag/index.js'
import { getEmailVerifiedFromLocalStorage } from 'src/utils/helper/localStorage.js'
import { getNavLockLevel, getPartialLockRouteNames, isNonActiveStatus } from 'src/utils/helper/contractTermination'
import { NAV_LOCK_LEVEL } from 'src/utils/constants/contract-termination'

import { routes } from './routes'

const router = createRouter({
  routes,
  history: createWebHistory()
})

const privateRoutes = routes.map(route => route.path).filter(route => route !== '/login_page' && route !== '/login')

const resetGlobalStore = () => {
  store.dispatch('submissions/reset').then(() => {
    store.commit('submissions/clearSubmissionsList')
    store.commit('binaries/clear')
    store.commit('binaries/emptyNewBinaries')
    store.commit('designers/clear')
    store.commit('graphics/clear')
    store.commit('tags/clear')
    store.commit('submissions/resetFormat')
    store.commit('bulkImport/clear')
  })
  SubmissionObject.reset()
}

function validateAgreementUrl(foundryId) {
  let foundriesList = JSON.parse(window.localStorage.getItem('userFoundries'));
  const foundry = foundriesList.find(f => f.id === foundryId);
  if (!foundry) return false;
  if (isNonActiveStatus(foundry.contractStatus)) return false;
  return foundry.contractSummary === 'UFDA 2';
}

router.beforeEach((to, from, next) => {
  /**
   * Auth?
   */
  if (privateRoutes.includes(to.path) && !store.getters['auth/isUserAuthenticated'] && to.name !== '404') {
    return next({ name: 'login' })
  }

  store.dispatch('overlay/hide')
  // when a unauthorized user tries to route on the authorized url
  // he/she will be not allowed to do so and url will be redirected to login
  if (from.name === 'login_page' && !store.getters['auth/isUserAuthenticated']) {
    return next(false)
  }

  if (store.getters['auth/isUserAuthenticated'] && !store.getters['auth/hasRole'](['foundrySupport'])) {
    const isUserAdmin = window.localStorage.getItem('isUserAdmin') || 'none'
    const foundriesTerminated = window.localStorage.getItem('foundriesTerminated') || 'none'
    const lockLevel = getNavLockLevel({ foundriesTerminated, isUserAdmin })

    const fullLockAllowlist = [TERMINATED_CONTRACT, 'login', 'login_page', 'logout']
    if (lockLevel === NAV_LOCK_LEVEL.FULL && !fullLockAllowlist.includes(to.name)) {
      return next({ name: TERMINATED_CONTRACT })
    }

    const partialBlockedRoutes = getPartialLockRouteNames()
    if (lockLevel === NAV_LOCK_LEVEL.PARTIAL && partialBlockedRoutes.includes(to.name)) {
      return next({ name: TERMINATED_CONTRACT })
    }
  }

  if (store.getters['auth/isUserAuthenticated'] &&
    (
      to.name === DASHBOARD ||
      to.name === EARNINGS_REPORT_DASHBOARD ||
      to.name === QUARTERLY_STATEMENTS ||
      to.name === PAYMENT_DASHBOARD ||
      to.name === PAYMENT_ACCOUNT ||
      to.name === PAYMENT_ACCOUNTS_NEW_MANAGE ||
      to.name === PAYMENT_ACCOUNTS_EDIT_MANAGE
    ) &&
    shouldRedirectToVerifyEmailPage(store)
  ) {
    return next({ name: VERIFY_EMAIL });
  }

  if(
    store.getters['auth/isUserAuthenticated'] && to.name === 'verify_email' && shouldRedirectToDashboard(store)
  ) {
    return next({ name: DASHBOARD });
  }

  // as user role management is not available yet, static check allow only selected users to access it
  if (to.name === BULK_IMPORT && !confirmBulkUser(store.getters['auth/user'].name)) {
    return next({ name: '403' })
  }
  if (to.name === 'impersonate' && window.localStorage.getItem('FS-Impersonate')) {
    window.history.replaceState({}, '', from.fullPath);
    return next(false);
  }
  if (to.path.startsWith('/sign-agreement/') && to.params.id) {
    if (validateAgreementUrl(to.params.id)) {
      return next({ name: '403' });
    }
    const currentSessionFoundryId = window.localStorage.getItem('sessionFoundryId') || ''
    if (to.params.id !== currentSessionFoundryId) {
      const foundriesList = JSON.parse(window.localStorage.getItem('userFoundries') || '[]')
      const foundry = foundriesList.find(f => f.id === to.params.id)
      if (foundry) {
        store.dispatch('auth/setSessionFoundry', { id: foundry.id, name: foundry.name })
      }
    }
  }

  store.dispatch('auth/cancelPendingRequests')
  resetGlobalStore()
  applyURLFeatureFlags(to)
  return next()
})

function updateLastOpenedPage(route) {
  if (!route?.name || ERROR_PAGES.includes(String(route.name))) return
  window.localStorage.setItem('prevComponentName', route.name)
  window.localStorage.setItem('prevComponentParams', route.params?.id ?? '')
  window.localStorage.setItem('prevComponentFormat', route.params?.format ?? '')
}


// Tracks whether a Profile API call is currently running so that quick back-to-back
// navigations don't fire overlapping requests. Reset in `.finally()` below.
let profileFetchInFlight = false;

// Once the server returns `emailVerified: true`, `auth/getUserDetails` persists it via
// persistLocalStorageFromProfileResponse, so getEmailVerifiedFromLocalStorage() flips to
// true and the guard below short-circuits — no further calls for the rest of the session.
router.afterEach((to) => {
  // Always remember the last opened page (for "resume where you left off"), regardless of auth.
  updateLastOpenedPage(to)
  // Skip if a Profile API call is already in flight (avoid duplicate concurrent requests
  // when the user navigates several times before the previous fetch resolves).
  if (profileFetchInFlight) return;

  // Skip if the user is logged out — nothing to fetch and the API would 401.
  if (!store.getters['auth/isUserAuthenticated']) return;

  // Skip if the user is already verified. We only want this running for UNVERIFIED users.
  // Source of truth is localStorage (kept in sync by login + Profile API responses).
  if (getEmailVerifiedFromLocalStorage()) return;

  // Only take the in-flight lock once we are about to dispatch. Setting it before the auth /
  // verified checks above would strand the flag on true (e.g. first navigation to login before
  // session is restored), and every later navigation would no-op — matching "works on refresh,
  // not when navigating in the SPA".
  profileFetchInFlight = true;

  // Dispatch the Profile API call. The action persists fresh emailVerified / gracePeriod
  // values to localStorage and rehydrates Vuex `auth` state on success.
  store.dispatch('auth/getUserDetails')
    // Swallow network/auth errors here — the action itself already commits a fallback
    // setAuth() from existing localStorage, and we don't want a rejected dispatch to
    // surface as an unhandled promise from the router.
    .catch((err) => {
      // Add a debug log to help trace profile fetch failures (network/auth/etc)
      // eslint-disable-next-line no-console
      console.log('[router:afterEach] Profile fetch error:', err);
    })
    .finally(() => {
      // Release the in-flight lock so the next navigation can fetch again if still unverified.
      profileFetchInFlight = false;
      // The user may have logged out while the request was pending; bail out.
      if (!store.getters['auth/isUserAuthenticated']) return;
      // Before each navigation, leaving verify-email is handled in beforeEach. If the user is
      // already on /verify-email and profile fetch corrects gracePeriodExpired, nothing would
      // re-run the guard; send them to the app when the server no longer requires verify-email.
      if (
        router.currentRoute.value.name === VERIFY_EMAIL &&
        shouldRedirectToDashboard(store)
      ) {
        router.replace({ name: DASHBOARD });
      }
    })
})

export { router, resetGlobalStore, validateAgreementUrl, updateLastOpenedPage }
